import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Check, Hammer } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEOHead from "@/components/SEOHead";
import FaqSection from "@/components/FaqSection";
import { photoDims } from "@/lib/photos";
import { track } from "@/lib/analytics";

const PAGE_PHOTOS = [
    { src: "/photos/hh-11.webp", alt: "Kitchen mid-renovation by Harris Homes, home remodeling in Tahlequah, OK" },
    { src: "/photos/hh-04.webp", alt: "Finished kitchen remodel by Harris Homes in Tahlequah, Oklahoma" },
    { src: "/photos/hh-19.webp", alt: "Bathroom remodel with custom finishes by Harris Homes, Tahlequah OK" },
];

const REMODEL_TYPES = [
    "Kitchen remodels",
    "Bathroom remodels",
    "Whole-home renovations",
    "Flooring and finish updates",
    "Selective demolition and re-layout",
];

const FAQS = [
    {
        q: "Do you take stand-alone remodels?",
        a: "Yes. Kitchen, bath, and whole-home remodels are their own projects for us — you don't need to be building a custom home to work with Harris Homes.",
    },
    {
        q: "Can we live in the home during the remodel?",
        a: "Usually, yes. We plan the work in phases, keep the job site clean, and agree on a schedule you can actually live with before work begins.",
    },
    {
        q: "How does pricing work for a remodel?",
        a: "After a walk-through you get a written scope and an itemized number. What moves the price most is the scope of demolition, plumbing or electrical changes, and your finish selections.",
    },
    {
        q: "How do we start?",
        a: "Call (918) 373-4341 or send a note through the contact form. We'll schedule a walk-through and tell you honestly what your budget can do.",
    },
];

export default function HomeRemodeling() {
    return (
        <div data-testid="home-remodeling-page">
            <SEOHead
                title="Home Remodeling in Tahlequah, OK | Harris Homes"
                description="Kitchen, bath and whole-home remodels in Tahlequah, OK by Harris Homes. One builder from design to final walk-through. Call 918-373-4341."
                canonical="https://harrishomesok.com/home-remodeling-tahlequah-ok"
            />

            {/* Header */}
            <section className="pt-20 md:pt-32 pb-16 md:pb-20 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="overline mb-6">
                            <MapPin size={11} strokeWidth={1.5} className="inline mr-2 -mt-0.5" style={{ color: "var(--hh-gold)" }} />
                            Tahlequah · Cherokee County
                        </div>
                        <h1 className="font-display text-5xl sm:text-7xl md:text-[6.5rem] leading-[0.95] tracking-tight max-w-5xl">
                            Home Remodeling in
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> Tahlequah, Oklahoma</span>
                        </h1>
                        <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            Kitchens, bathrooms, and whole-home remodels for the house you
                            already love. You work with one builder from the first walk-through
                            to the final punch list — the same hands and the same standards we
                            bring to our custom homes.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/contact" data-testid="remodeling-hero-cta" className="btn-gold">
                                Request a Remodel Quote <ArrowRight size={14} />
                            </Link>
                            <Link to="/services" data-testid="remodeling-services-link" className="btn-ghost">
                                All Services
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Remodel types */}
            <section className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--hh-bg-2)" }}>
                <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
                    <Reveal className="md:col-span-6">
                        <div className="overline mb-5">Scope</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
                            Remodels we do
                        </h2>
                        <div className="gold-rule w-24 my-8" />
                        <ul className="space-y-3 max-w-md">
                            {REMODEL_TYPES.map((item) => (
                                <li key={item} className="flex items-start gap-3 text-sm md:text-base" style={{ color: "var(--hh-text)" }}>
                                    <Check size={16} strokeWidth={1.6} style={{ color: "var(--hh-gold)", marginTop: 2 }} />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.12} className="md:col-span-6">
                        <div className="overline mb-5">The Big One</div>
                        <h3 className="font-display text-3xl md:text-4xl leading-[1.1] tracking-tight flex items-center gap-3">
                            <Hammer size={24} strokeWidth={1.2} style={{ color: "var(--hh-gold)" }} />
                            Whole-home renovations
                        </h3>
                        <div className="gold-rule w-24 my-8" />
                        <div className="space-y-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            <p>
                                For older homes with good bones, a whole-home renovation resets
                                layout, finishes, and function at once — often for less than
                                starting over somewhere new.
                            </p>
                            <p>
                                It fits families who love their location and their lot, and want
                                the house to finally catch up.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Process + cost */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
                    <Reveal className="md:col-span-6">
                        <div className="overline mb-5">The Process</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
                            How a remodel runs
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> with us.</span>
                        </h2>
                        <div className="gold-rule w-24 my-8" />
                        <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            It starts with a walk-through of your home and a written scope of
                            work — no vague allowances. You get a schedule before we start, a
                            clean job site while we work, and a plan for living in the home
                            during the project whenever that's possible.
                        </p>
                    </Reveal>
                    <Reveal delay={0.12} className="md:col-span-6">
                        <div className="overline mb-5">Budget</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
                            Cost factors,
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> up front.</span>
                        </h2>
                        <div className="gold-rule w-24 my-8" />
                        <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            Remodel pricing moves with demolition scope, plumbing and electrical
                            changes, and the finishes you choose. We price your actual project
                            after seeing your actual home — then put the number in writing.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Photos */}
            <section className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--hh-bg-2)" }}>
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                            <div>
                                <div className="overline mb-5">Our Work</div>
                                <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight max-w-2xl">
                                    Kitchens, baths, and finishes by our crew.
                                </h2>
                            </div>
                            <Link to="/projects" data-testid="remodeling-projects-link" className="btn-ghost self-start">
                                Full Gallery <ArrowRight size={14} />
                            </Link>
                        </div>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-3 md:gap-4">
                        {PAGE_PHOTOS.map((p, i) => {
                            const d = photoDims(p.src);
                            return (
                                <Reveal key={p.src} delay={i * 0.08}>
                                    <div className="relative img-zoom h-[280px] md:h-[340px]">
                                        <img
                                            src={p.src}
                                            alt={p.alt}
                                            width={d?.w}
                                            height={d?.h}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <FaqSection
                overline="Frequently Asked"
                headingLead="Questions people ask"
                headingItalic="about remodels."
                items={FAQS}
                testId="remodeling-faq"
            />

            {/* CTA */}
            <section className="py-28 md:py-40 px-6 md:px-10 text-center">
                <Reveal>
                    <div className="overline mb-5">Request a Remodel Quote</div>
                    <h2 className="font-display text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl mx-auto">
                        Love your home, but
                        <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> not your kitchen?</span>
                    </h2>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link to="/contact" data-testid="remodeling-final-cta" onClick={() => track.ctaClick("Request a Remodel Quote", "remodeling-final")} className="btn-gold">
                            Request a Quote <ArrowRight size={14} />
                        </Link>
                        <a href="tel:+19183734341" data-testid="remodeling-final-phone" onClick={() => track.phoneClick("remodeling-final")} className="btn-ghost">
                            <Phone size={14} strokeWidth={1.5} /> (918) 373-4341
                        </a>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
